/**
 * Os limites do contrato em Pessoas: o que o cliente vê (#765, fase 5 e
 * armazenamento).
 *
 * O administrador vê sempre quantas vagas tem. No limite de usuários, o
 * servidor recusa o convite e a tela mostra o preço deste cliente antes de
 * contratar; perto de 90% do armazenamento, a faixa avisa e oferece mais
 * espaço do mesmo jeito. Quem não é administrador vê o mesmo aviso, com "fale
 * com o administrador da organização" no lugar do botão.
 *
 * ── Prontos antes do back ─────────────────────────────────────────────────
 *
 * Decisão de 26/10/2026: as peças ficam prontas, e Pessoas passa a usá-las
 * quando a API mandar os limites (fase 5, #768; medição, #716; mais
 * armazenamento, #718). Até lá Pessoas continua como está. Por isso cada peça
 * recebe tudo pelas props.
 *
 * A mensagem que chega do cliente nunca é bloqueada pelo limite: só o envio de
 * arquivos pela equipe para. A faixa diz isso, para ninguém achar que a
 * organização parou de receber conversa.
 */

import type { ReactNode } from 'react';
import { Botao, Dialogo, Icone, Medidor } from '@/shared/ui';
import { comUmaCasa, dinheiroComCentavos } from '@/shared/lib/formato';
import { Faixa } from '@/shared/ui/estados/faixa';
import type { Dinheiro } from '../model/limites-da-organizacao';
import { avisoDoArmazenamento } from '../lib/limites-da-organizacao';
import './limites-da-organizacao.css';

/** As vagas da organização, ao lado do título de Pessoas. */
export function VagasDaOrganizacao({
  emUso,
  contratados,
}: {
  emUso: number;
  contratados: number;
}): ReactNode {
  return (
    <span className="limites-da-organizacao__vagas">
      <Medidor
        nome="Usuários"
        usado={emUso}
        total={contratados}
        rotulo={`${emUso} de ${contratados === 1 ? '1 usuário' : `${contratados} usuários`}`}
      />
    </span>
  );
}

/**
 * A faixa do armazenamento: só a partir de 90%, e a confirmação verde logo
 * depois de contratar mais espaço. O aviso vem antes da confirmação: o bloco
 * que não bastou para sair dos 90% (ou do limite) não pode virar verde, nem
 * esconder o botão do próximo.
 */
export function FaixaDoArmazenamento({
  emUsoGb,
  contratadoGb,
  tamanhoDoBlocoGb,
  podeContratar,
  aoContratar,
  acabouDeContratar = false,
  recado,
}: {
  emUsoGb: number | null;
  contratadoGb: number;
  tamanhoDoBlocoGb: number;
  /** Só o administrador contrata; os outros veem quem procurar. */
  podeContratar: boolean;
  aoContratar: () => void;
  /** A contratação acabou de dar certo: a faixa confirma em verde. */
  acabouDeContratar?: boolean;
  /**
   * Com quem falar, para quem não pode contratar. Sem ele, a faixa manda falar
   * com o administrador da organização.
   */
  recado?: string;
}): ReactNode {
  const aviso = avisoDoArmazenamento(emUsoGb, contratadoGb);
  if (acabouDeContratar && emUsoGb !== null && !aviso) {
    return (
      <Faixa tom="ok" icone={<Icone nome="nuvem" />}>
        <strong className="faixa__titulo">
          Armazenamento: {comUmaCasa(emUsoGb)} de {comUmaCasa(contratadoGb)} GB.
        </strong>
        <span className="faixa__apoio">Os {tamanhoDoBlocoGb} GB a mais já valem.</span>
      </Faixa>
    );
  }
  if (!aviso) return null;
  return (
    <Faixa
      tom="atencao"
      icone={<Icone nome="nuvem" />}
      acao={
        podeContratar
          ? { rotulo: `Contratar mais ${tamanhoDoBlocoGb} GB`, aoClicar: aoContratar }
          : undefined
      }
    >
      <strong className="faixa__titulo">{aviso.titulo}</strong>
      <span className="faixa__apoio">
        {/* O bloco entrou, mas não bastou: sem isto, só o total mudaria, e
            quem não percebe a troca do número contrataria outro achando que falhou. */}
        {acabouDeContratar
          ? `Os ${tamanhoDoBlocoGb} GB a mais já valem, e ainda não bastam para sair do aviso. `
          : null}
        {aviso.noLimite
          ? 'O envio de arquivos parou. As mensagens dos clientes continuam chegando.'
          : 'No limite, o envio de arquivos para. As mensagens dos clientes continuam chegando.'}
        {podeContratar
          ? null
          : ` ${recado ?? 'Fale com o administrador da organização para contratar mais.'}`}
      </span>
    </Faixa>
  );
}

/**
 * A recusa do convite, ou da reativação, no limite. O preço é o deste cliente,
 * e vem na recusa: a pessoa sabe quanto vai pagar antes de aceitar. Quando não
 * dá para contratar, o texto é o do servidor, que sabe por quê (acima do
 * limite, sem o valor definido, ou pela gestão).
 */
export function DialogoDoLimite({
  contratados,
  valorDoAdicional,
  podeContratar,
  verbo = 'convidar',
  valorMudou = false,
  recado = null,
  enviando,
  aoContratar,
  aoFechar,
}: {
  contratados: number;
  valorDoAdicional: Dinheiro | null;
  podeContratar: boolean;
  /** O que acontece junto com a contratação: o convite ou a reativação. */
  verbo?: 'convidar' | 'reativar';
  /** O valor que a pessoa tinha visto mudou; o novo é `valorDoAdicional`. */
  valorMudou?: boolean;
  /** O texto do servidor, quando não dá para contratar. */
  recado?: string | null;
  /** A contratação foi para o servidor e a resposta ainda não voltou. */
  enviando: boolean;
  aoContratar: () => void;
  aoFechar: () => void;
}): ReactNode {
  const preco = dinheiroComCentavos(valorDoAdicional);
  // Sem preço no contrato, não há o que aceitar: quem define o valor é a Cliqfy.
  const contrata = podeContratar && preco !== null;
  const acao = verbo === 'reativar' ? 'reativar' : 'adicionar';
  const contratadosEmPalavras =
    contratados === 1 ? '1 usuário contratado' : `${contratados} usuários contratados`;
  // Fechar não desfaz a contratação que já está no servidor (#1160): enquanto
  // ela roda, Esc, o véu e o Cancelar não fecham.
  const fechar = () => {
    if (!enviando) aoFechar();
  };
  return (
    <Dialogo
      titulo="Limite de usuários atingido"
      aoFechar={fechar}
      botoes={
        contrata ? (
          <>
            <Botao
              variante="fantasma"
              desabilitado={enviando}
              motivo="Contratando o usuário adicional"
              onClick={fechar}
            >
              Cancelar
            </Botao>
            <Botao
              variante="primario"
              desabilitado={enviando}
              motivo="Contratando o usuário adicional"
              onClick={aoContratar}
            >
              {enviando
                ? 'Contratando…'
                : verbo === 'reativar'
                  ? 'Contratar e reativar'
                  : 'Contratar e convidar'}
            </Botao>
          </>
        ) : (
          <Botao variante="primario" onClick={aoFechar}>
            Entendi
          </Botao>
        )
      }
    >
      <p className="dialogo__texto">
        {contrata
          ? `${valorMudou ? 'O valor do usuário adicional deste contrato mudou. ' : ''}Você atingiu o limite de ${contratadosEmPalavras}. Para ${acao} esta pessoa, você estará contratando 1 usuário adicional por ${preco} por mês.`
          : (recado ??
            (podeContratar
              ? `Você atingiu o limite de ${contratadosEmPalavras}. O valor do usuário adicional deste contrato ainda não foi definido: fale com a equipe da Cliqfy.`
              : `A organização atingiu o limite de ${contratadosEmPalavras}. Fale com o administrador da organização para contratar mais.`))}
      </p>
    </Dialogo>
  );
}

/** Mais um bloco de armazenamento, com o preço deste cliente. */
export function DialogoDeMaisArmazenamento({
  tamanhoDoBlocoGb,
  valorDoBloco,
  enviando,
  aoContratar,
  aoFechar,
}: {
  tamanhoDoBlocoGb: number;
  valorDoBloco: Dinheiro | null;
  enviando: boolean;
  aoContratar: () => void;
  aoFechar: () => void;
}): ReactNode {
  const preco = dinheiroComCentavos(valorDoBloco);
  // Fechar não desfaz a contratação que já está no servidor: enquanto ela
  // roda, Esc, o véu e o Cancelar não fecham.
  const fechar = () => {
    if (!enviando) aoFechar();
  };
  return (
    <Dialogo
      titulo={`Contratar mais ${tamanhoDoBlocoGb} GB`}
      aoFechar={fechar}
      botoes={
        preco ? (
          <>
            <Botao
              variante="fantasma"
              desabilitado={enviando}
              motivo="Contratando o armazenamento"
              onClick={fechar}
            >
              Cancelar
            </Botao>
            <Botao
              variante="primario"
              desabilitado={enviando}
              motivo="Contratando o armazenamento"
              onClick={aoContratar}
            >
              {enviando ? 'Contratando…' : 'Contratar'}
            </Botao>
          </>
        ) : (
          <Botao variante="primario" onClick={aoFechar}>
            Entendi
          </Botao>
        )
      }
    >
      <p className="dialogo__texto">
        {preco
          ? `Você estará contratando mais ${tamanhoDoBlocoGb} GB por ${preco} por mês. O novo limite vale na hora.`
          : 'O valor do armazenamento adicional deste contrato ainda não foi definido: fale com a equipe da Cliqfy.'}
      </p>
    </Dialogo>
  );
}
